import merge from 'lodash/merge';
import includes from 'lodash/includes';
import {Types} from './actions';
import initialState from './initialState';
import {fromSources} from 'observables/lettables/pickFile';
import ReducerBuilder from "../../classes/ReducerBuilder";
import {fillImageDataFromFile} from './utils';
import {isNoU, TempImageType} from "../../utils/DBUtils";

// Shared between CREATE_IMAGE and NATIVE_UPLOAD_IMAGE -- both kick off an image creation
// that should show the "creating image" spinner (hoc/BreakImagePicker.jsx's isCreating,
// cleared by CREATED_IMAGE either way).
const reduceCreatingImage = (state, action) => {
    state.creatingImage = true;
    if (!isNoU(action.forResourceId) && !isNoU(action.field)) {
        state.creatingImageFor = {
            id: action.forResourceId.id,
            field: action.field
        };
    }
};

const imagesReducer = ReducerBuilder.make(initialState)
    .forType(Types.PICK_ORIGINAL)
    .reduce((state, action) => {
        state.pickingImage = true;
        state.pickingOriginalForResourceId = action.forResourceId;
    })
    .forType(Types.PICKED_ORIGINAL)
    .reduce((state, action) => {
        state.pickingImage = false;

        const originalFile = action.pendingOriginalFile;

        // If we have a pending image to upload create the mock data
        // id - pending key is what file stack will have uploaded the image as to our temp s3 bucket
        // imageType - using a client only value to resolve the preview url correctly
        // height and width - direct reference to height and width from file stack

        // default values will be filled in from the file data provided by file stack
        // any that are not will remain null to be edited by user
        const pendingImage = fillImageDataFromFile({
            id: originalFile.key,
            key: originalFile.key,
            uploadId: originalFile.uploadId,
            url: originalFile.url,

            imageType: TempImageType,

            altAttribute: originalFile.altAttribute || '',
            link: originalFile.link || '',

            // ignore whatever FileStackClient.pick prefilled for this value
            source: includes(fromSources, originalFile.source) ? '' : (originalFile.source || ''),

            height: originalFile.height,
            width: originalFile.width
        }, originalFile);

        state.pendingImages.push(pendingImage);

        if (!state.selectedPendingImage) {
            state.selectedPendingImage = state.pendingImages[0];
            state.selectedPendingImageIndex = 0;
        }
    })
    .forType(Types.SET_EXISTING_IMAGE)
    .reduce((state, action) => {
        state.pendingImages.push(action.existingImage);
        state.selectedPendingImage = state.pendingImages[0];
    })
    .forType(Types.CHANGE_SELECTION)
    .reduce((state, action) => {
        state.selectedPendingImage = state.pendingImages[action.nextSelectionIndex];
        state.selectedPendingImageIndex = action.nextSelectionIndex;
    })
    .forType(Types.UPDATE_PENDING_IMAGE_UPLOAD)
    .reduce((state, action) => {
        const selectedIndex = state.selectedPendingImageIndex;

        if (typeof state.pendingImages[selectedIndex] != 'undefined') {
            state.pendingImages[selectedIndex][action.field] = action.value;
            state.selectedPendingImage = state.pendingImages[selectedIndex];    

            // clear any validation errors associated with this field.
            if (Object.keys(state.imageEditFormErrors).length > 0) {
                delete state.imageEditFormErrors[action.field];
            }
        }

    })
    .forType(Types.SHOW_IMAGE_EDIT_FORM_ERRORS)
    .reduce((state, action) => {
        state.imageEditFormErrors = action.errors;
        state.showImageEditFormErrors = true;
    })
    .forType(Types.UPLOAD_ORIGINAL)
    .reduce((state, action) => {
        state.creatingImage = true;
        state.uploadingImage = true;
        state.originalImageData = action.imageInfo;
    })
    .forType(Types.UPLOAD_SUCCESS)
    .reduce((state, action) => {
        state.uploadingImage = false;

        const selectedIndex = state.selectedPendingImageIndex;
        const isBulkUpload = state.pendingImages.length > 1;

        if (isBulkUpload) {
            // if the user:
            //   - selected multiple images to upload,
            //   - filled the `source` field on only one form, and
            //   - submitted that one form,
            //
            // then copy that same `source` value to all the other forms in order to
            // pre-populate that field in each one.

            const sourcesToIgnore = fromSources.concat(['', null, undefined]);

            const noOtherSources = state.pendingImages
                .filter((image, index) => index !== selectedIndex)
                .every(pending => includes(sourcesToIgnore, pending.source));

            if (noOtherSources) {
                const {source} = state.selectedPendingImage;

                state.pendingImages = state.pendingImages.map((pending, index) => (
                    index === selectedIndex ? pending : merge({}, pending, {source})
                ));
            }
        }

        // set a flag to indicate that this image was uploaded.
        state.pendingImages[selectedIndex].uploaded = true;

        // save the id returned from the API.
        state.pendingImages[selectedIndex].id = action.image.id;

        state.selectedPendingImage = state.pendingImages[selectedIndex];

        // increment selected index to help advance the carousel.
        if ((selectedIndex + 1) < state.pendingImages.length) {
            state.selectedPendingImageIndex = (selectedIndex + 1);
        }
    })
    .forType(Types.UPLOADED_ORIGINAL)
    .reduce((state, action) => {
        state.creatingImage = false;
        state.originalImageData = null;

        // don't clear pending image data yet if uploading to a RetailerItem.
        if (state.pickingOriginalForResourceId) {
            state.pendingImages.length = 0;
            state.selectedPendingImage = null;
            state.selectedPendingImageIndex = 0;
        }

        state.pickingOriginalForResourceId = null;
        state.createAfterOriginal = null;
        state.uploadingImage = false;
    })
    .forType(Types.SET_UPLOADING_IMAGES_FOR)
    .reduce((state, action) => {
        state.uploadingImagesFor = action.uploadingImagesFor;
    })
    .forType(Types.END_UPLOAD_ORIGINAL)
    .reduce((state) => {
        state.creatingImage = false;
        state.originalImageData = null;
        state.pendingImages.length = 0;
        state.selectedPendingImage = null;
        state.selectedPendingImageIndex = 0;
        state.pickingOriginalForResourceId = null;
        state.createAfterOriginal = null;
        state.creatingImageFor = null;
        state.uploadingImage = false;
    })
    .forType(Types.UPLOAD_ORIGINAL_AND_CREATE_IMAGE)
    .reduce((state, action) => {
        state.createAfterOriginal = {
            ...action
        };
    })
  .forType(Types.UPLOAD_ORIGINAL_AND_GENERATE_IMAGES)
  .reduce((state, action) => {
      state.generateAfterOriginal = {
        ...action
      };
  })
  .forType(Types.STARTING_GENERATE_IMAGES)
  .reduce((state, action) => {
    state.generatingImages = true;
  })
  .forType(Types.END_GENERATE_IMAGES)
  .reduce((state, action) => {
      state.generatingImages = false;
      state.generateAfterOriginal = null;
  })
    .forType(Types.CREATE_IMAGE)
    .reduce(reduceCreatingImage)
    .forType(Types.OPEN_NATIVE_UPLOAD_MODAL)
    .reduce((state, action) => {
        state.nativeUploadContext = {...action};
    })
    .forType(Types.CLOSE_NATIVE_UPLOAD_MODAL)
    .reduce((state) => {
        state.nativeUploadContext = null;
    })
    .forType(Types.OPEN_NATIVE_RECROP_MODAL)
    .reduce((state, action) => {
        state.nativeRecropContext = {...action};
    })
    .forType(Types.CLOSE_NATIVE_RECROP_MODAL)
    .reduce((state) => {
        state.nativeRecropContext = null;
    })
    .forType(Types.NATIVE_UPLOAD_IMAGE)
    .reduce((state, action) => {
        // the modal has done its job once this fires; the spinner driven by
        // creatingImage/creatingImageFor (see hoc/BreakImagePicker.jsx) takes over.
        // Fired by either ImageUploadModalContainer or ImageRecropModalContainer --
        // only one of the two contexts is ever set at a time, so clearing both here
        // unconditionally is safe and keeps this reducer case uniform for either caller.
        state.nativeUploadContext = null;
        state.nativeRecropContext = null;
        reduceCreatingImage(state, action);
    })
    .forType(Types.CREATED_IMAGE)
    .reduce((state, action) => {
        state.creatingImage = false;
        state.selectedImageId = action.image.id;
        state.creatingImageFor = null;
        state.createAfterOriginal = null;
    })
    .forType(Types.START_EDIT_IMAGE)
    .reduce((state, action) => {
        state.selectedImageId = action.imageId;
        state.editingImage = true;
    })
    .forType(Types.END_EDIT_IMAGE)
    .reduce((state) => {
        state.creatingImage = false;
        state.editingImage = false;
        state.selectedImageId = null;
        state.creatingImageFor = null;

        if (!state.pickingOriginalForResourceId && state.pendingImages.length === 1) {
            // clear these if it's a RetailerItem image.
            state.pendingImages.length = 0;
            state.selectedPendingImage = null;
            state.selectedPendingImageIndex = 0;
        }
    })
    .forType(Types.END_THEN_EDIT_IMAGE)
    .reduce((state) => {
        state.creatingImage = false;
        state.editingImage = false;
        state.selectedImageId = null;
    })
    .forType(Types.SAVE_IMAGE_DATA)
    .reduce((state) => {
        state.savingImageData = true;
    })
    .forType(Types.SAVED_IMAGE_DATA)
    .reduce((state) => {
        state.savingImageData = false;

        if (state.pendingImages.length > 1) {
            const nextIndex = state.selectedPendingImageIndex + 1;
            state.selectedPendingImage = state.pendingImages[nextIndex];
            state.selectedPendingImageIndex = nextIndex;
        }
    })
    .forType(Types.OPEN_CONFIRM_CANCEL_MODAL)
    .reduce((state, action) => {
        state.isConfirmCancelModalOpen = true;
    })
    .forType(Types.CLOSE_CONFIRM_CANCEL_MODAL)
    .reduce((state, action) => {
        state.isConfirmCancelModalOpen = false;
    })
    .forType(Types.RECEIVED_SIBLING_IMAGES)
    .reduce((state, action) => {
        state.selectedImageSiblings = action.siblings
            .filter(image => image.id !== state.selectedImageId)
            .map(image => image.id);
    })
    .build();

export default imagesReducer;



// WEBPACK FOOTER //
// ./public/pages/Images/reducer.js